import React from "react";

export type StatusTone = "success" | "warning" | "danger" | "neutral" | "info";

export const STATUS_TONE_CLASSES: Record<
  StatusTone,
  { dot: string; ping: string; text: string }
> = {
  success: {
    dot: "bg-green-500",
    ping: "bg-green-500",
    text: "text-green-600 dark:text-green-500",
  },
  warning: {
    dot: "bg-yellow-500",
    ping: "bg-yellow-500",
    text: "text-yellow-600 dark:text-yellow-500",
  },
  danger: {
    dot: "bg-red-500",
    ping: "bg-red-500",
    text: "text-red-600 dark:text-red-500",
  },
  neutral: {
    dot: "bg-gray-400 dark:bg-gray-600",
    ping: "bg-gray-400 dark:bg-gray-600",
    text: "text-gray-500 dark:text-gray-400",
  },
  info: {
    dot: "bg-blue-500",
    ping: "bg-blue-500",
    text: "text-blue-600 dark:text-blue-400",
  },
};

// Alpha (v7) tone mapping. The v7 namespace has no yellow or blue token, so
// warning maps to bronze-dark and info to the ink family by design.
export const ALPHA_STATUS_TONE_CLASSES: Record<
  StatusTone,
  { dot: string; ping: string; text: string }
> = {
  success: {
    dot: "bg-v7-success",
    ping: "bg-v7-success",
    text: "text-v7-success",
  },
  warning: {
    dot: "bg-v7-bronze-dark",
    ping: "bg-v7-bronze-dark",
    text: "text-v7-bronze-dark",
  },
  danger: {
    dot: "bg-v7-rust",
    ping: "bg-v7-rust",
    text: "text-v7-rust",
  },
  neutral: {
    dot: "bg-v7-ink/40",
    ping: "bg-v7-ink/40",
    text: "text-v7-ink/60",
  },
  info: {
    dot: "bg-v7-ink",
    ping: "bg-v7-ink",
    text: "text-v7-ink-soft",
  },
};

export interface StatusDotProps {
  label?: string;
  tone: StatusTone;
  pulse?: boolean;
  className?: string;
  appearance?: "default" | "alpha";
}

const StatusDot: React.FC<StatusDotProps> = ({
  label,
  tone,
  pulse = false,
  className = "",
  appearance = "default",
}) => {
  const classes = appearance === "alpha"
    ? ALPHA_STATUS_TONE_CLASSES[tone]
    : STATUS_TONE_CLASSES[tone];

  return (
    <span
      className={`relative flex h-3 w-3 shrink-0 items-center justify-center ${className}`.trim()}
      aria-label={label}
      role={label ? "img" : undefined}
    >
      {pulse && (
        <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-40 ${classes.ping}`} />
      )}
      <span className={`relative inline-flex h-3 w-3 rounded-full ${classes.dot}`} />
    </span>
  );
};

export default StatusDot;
